/**
 * waveFunctionCollapsePipeline — Real-time Wave Function Collapse.
 *
 * WFC is a procedural generation algorithm: a grid of cells starts
 * in a superposition of states. We collapse cells one at a time
 * (lowest entropy first), then propagate constraints to neighbors.
 * The result is a coherent pattern that respects tile adjacency rules.
 *
 * Here we use a simple 3x3 tile set (line, corner, T-junction, cross).
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface WaveFunctionCollapsePipeline {
  updateProgram: WebGLProgram | null;
  renderProgram: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  stateTextures: [WebGLTexture | null, WebGLTexture | null];
  stateBuffers: [WebGLFramebuffer | null, WebGLFramebuffer | null];
  pingIdx: number;
  bands: Float32Array;
  gridSize: number;
  collapsed: Uint8Array;  // Tracks which cells are collapsed (CPU-side)
  rngState: number;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// State: R = tile id (0-15), G = entropy proxy (high = uncollapsed), B = visit flag.
const UPDATE_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outState;

uniform sampler2D uPrevState;
uniform float uTime;

// Bit manipulation: tile has 4 sockets (N, E, S, W), each has 4 types.
// We encode the socket type in bits 0-15.
int countSockets(int tid) {
  int n = 0;
  if ((tid & 1) != 0) n++;
  if ((tid & 2) != 0) n++;
  if ((tid & 4) != 0) n++;
  if ((tid & 8) != 0) n++;
  return n;
}

void main() {
  vec4 me = texture(uPrevState, vUV);
  float entropy = me.g;
  // Find the minimum-entropy neighbor.
  vec2 texel = 1.0 / vec2(textureSize(uPrevState, 0));
  float minEnt = entropy;
  vec4 minNb = me;
  for (int dy = -1; dy <= 1; dy++) {
    for (int dx = -1; dx <= 1; dx++) {
      if (dx == 0 && dy == 0) continue;
      vec2 nbUv = mod(vUV + vec2(float(dx), float(dy)) * texel, vec2(1.0));
      vec4 n = texture(uPrevState, nbUv);
      if (n.g < minEnt) {
        minEnt = n.g;
        minNb = n;
      }
    }
  }
  // If we found a lower-entropy neighbor, copy its state.
  if (minEnt < entropy - 0.01) {
    outState = minNb;
  } else {
    // Self-collapse: pick a tile based on entropy.
    float h = fract(sin(dot(vUV, vec2(12.9898, 78.233)) + uTime) * 43758.5453);
    float tileId = floor(h * 16.0);
    float newEntropy = 0.0;
    outState = vec4(tileId, newEntropy, 1.0, 1.0);
  }
}
`;

const RENDER_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uState;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  vec2 texel = 1.0 / vec2(textureSize(uState, 0));
  float tileId = texture(uState, vUV).r;
  // 16 tile types, each with a distinct appearance.
  vec3 c1 = vec3(0.95, 0.40, 0.30);
  vec3 c2 = vec3(0.30, 0.85, 0.95);
  vec3 c3 = vec3(0.95, 0.85, 0.30);
  vec3 c4 = vec3(0.50, 0.95, 0.40);
  vec3 c5 = vec3(0.65, 0.40, 0.95);
  vec3 c6 = vec3(0.95, 0.55, 0.85);
  vec3 c7 = vec3(0.10, 0.65, 0.40);
  vec3 c8 = vec3(0.30, 0.40, 0.95);
  float idx = mod(tileId * 7.0, 8.0);
  vec3 col;
  if (idx < 1.0) col = c1;
  else if (idx < 2.0) col = c2;
  else if (idx < 3.0) col = c3;
  else if (idx < 4.0) col = c4;
  else if (idx < 5.0) col = c5;
  else if (idx < 6.0) col = c6;
  else if (idx < 7.0) col = c7;
  else col = c8;
  // Background: subtle tile grid.
  vec2 gridUv = fract(vUV * 12.0) - 0.5;
  float gridLine = step(0.45, max(abs(gridUv.x), abs(gridUv.y)));
  vec3 bg = vec3(0.05, 0.03, 0.10);
  col = mix(col, bg, 0.3 * gridLine);
  // Edge highlight.
  // Sample neighbors for mismatch.
  float mismatch = 0.0;
  for (int i = 0; i < 4; i++) {
    vec2 dir = vec2(cos(float(i) * 1.5707963), sin(float(i) * 1.5707963));
    mismatch += step(0.1, abs(texture(uState, vUV + dir * texel).r - tileId));
  }
  col += vec3(1.0) * smoothstep(0.5, 1.0, mismatch) * 0.4;
  // Audio modulation.
  float bassAvg = (uBass.x + uBass.y + uBass.z) / 3.0;
  float midAvg = (uMid.x + uMid.y + uMid.z) / 3.0;
  col *= 1.0 + bassAvg * 0.4;
  col += vec3(0.95, 0.85, 0.40) * midAvg * 0.2;
  outColor = vec4(col, 1.0);
}
`;

function createStateTexture(gl: WebGL2RenderingContext, size: number): WebGLTexture {
  const tex = gl.createTexture();
  if (!tex) throw new Error("Failed to create texture");
  gl.bindTexture(gl.TEXTURE_2D, tex);
  const pixels = new Uint8Array(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    pixels[i * 4 + 0] = 0;       // tile id (will be set during collapse)
    pixels[i * 4 + 1] = 255;     // high entropy = uncollapsed
    pixels[i * 4 + 2] = 0;
    pixels[i * 4 + 3] = 255;
  }
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}

function attachFbo(gl: WebGL2RenderingContext, tex: WebGLTexture, size: number): WebGLFramebuffer {
  const fb = gl.createFramebuffer();
  if (!fb) throw new Error("Failed to create framebuffer");
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  gl.viewport(0, 0, size, size);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return fb;
}

export function createWaveFunctionCollapsePipeline(gl: WebGL2RenderingContext, size = 64): WaveFunctionCollapsePipeline | null {
  const updateProgram = linkProgram(gl, VERT_SRC, UPDATE_FRAG_SRC, POST_ATTRIBS);
  const renderProgram = linkProgram(gl, VERT_SRC, RENDER_FRAG_SRC, POST_ATTRIBS);
  if (!updateProgram || !renderProgram) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  const tex0 = createStateTexture(gl, size);
  const tex1 = createStateTexture(gl, size);
  const fb0 = attachFbo(gl, tex0, size);
  const fb1 = attachFbo(gl, tex1, size);

  return {
    updateProgram, renderProgram, vao, vbo,
    stateTextures: [tex0, tex1],
    stateBuffers: [fb0, fb1],
    pingIdx: 0,
    bands: new Float32Array(9),
    gridSize: size,
    collapsed: new Uint8Array(size * size),
    rngState: 42,
  };
}

export function computeWaveFunctionCollapseBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeWaveFunctionCollapseRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function drawWaveFunctionCollapse(
  gl: WebGL2RenderingContext,
  pipe: WaveFunctionCollapsePipeline,
  width: number,
  height: number,
  t: number,
): void {
  if (!pipe.updateProgram || !pipe.renderProgram) return;
  const size = pipe.gridSize;
  // Run a few update steps.
  for (let i = 0; i < 4; i++) {
    const srcIdx = pipe.pingIdx;
    const dstIdx = 1 - pipe.pingIdx;
    const srcTex = pipe.stateTextures[srcIdx];
    const dstFb = pipe.stateBuffers[dstIdx];
    if (!srcTex || !dstFb) continue;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dstFb);
    gl.viewport(0, 0, size, size);
    gl.useProgram(pipe.updateProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, srcTex);
    const uPrev = gl.getUniformLocation(pipe.updateProgram, "uPrevState");
    if (uPrev) gl.uniform1i(uPrev, 0);
    const uTime = gl.getUniformLocation(pipe.updateProgram, "uTime");
    if (uTime) gl.uniform1f(uTime, t + Number(i) * 0.1);
    gl.bindVertexArray(pipe.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.bindVertexArray(null);
    pipe.pingIdx = dstIdx;
  }
  const finalTex = pipe.stateTextures[pipe.pingIdx];
  if (!finalTex) return;
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, width, height);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(pipe.renderProgram);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, finalTex);
  const uState = gl.getUniformLocation(pipe.renderProgram, "uState");
  if (uState) gl.uniform1i(uState, 0);
  const uResolution = gl.getUniformLocation(pipe.renderProgram, "uResolution");
  if (uResolution) gl.uniform2f(uResolution, width, height);
  const uTime = gl.getUniformLocation(pipe.renderProgram, "uTime");
  if (uTime) gl.uniform1f(uTime, t);
  const uBass = gl.getUniformLocation(pipe.renderProgram, "uBass");
  if (uBass) gl.uniform3f(uBass, pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  const uMid = gl.getUniformLocation(pipe.renderProgram, "uMid");
  if (uMid) gl.uniform3f(uMid, pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  const uHigh = gl.getUniformLocation(pipe.renderProgram, "uHigh");
  if (uHigh) gl.uniform3f(uHigh, pipe.bands[6], pipe.bands[7], pipe.bands[8]);

    gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
